import { Fragment, useContext } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';
import CodeIcon from '@mui/icons-material/Code';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import { TransitionContext, TRANSITION_DEMO_USERS } from '~/providers/TransitionProvider';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import { useIntakeQueue } from '~/providers/IntakeQueueProvider';
import { ReferralSource } from '~/types/transition';

export const TransitionDemoContent: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(TransitionContext);

  if (!context) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Navigate to the Transition section to use these controls.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/transition/pipeline')}>
          Go to Transition
        </Button>
      </Box>
    );
  }

  const { simulateIntake, resetAll, currentDemoUser, switchDemoUser } = context;
  const { clearAll } = useTransitionAttention();
  const { clearAll: clearIntakeQueue } = useIntakeQueue();

  return (
    <Box sx={{ pt: 1 }}>
      {/* Current persona */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <PersonIcon sx={{ fontSize: 36 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>{currentDemoUser.name}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>{currentDemoUser.role}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
          {currentDemoUser.access.includes('transition') && (
            <Chip label="Transition" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
          )}
          {currentDemoUser.access.includes('operate') && (
            <Chip label="Operate" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
          )}
        </Box>
      </Box>

      {/* Switch persona */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
        SWITCH PERSONA
      </Typography>
      <List dense sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, py: 0, mb: 2 }}>
        {TRANSITION_DEMO_USERS.map((user, index) => (
          <Fragment key={user.id}>
            {index > 0 && <Divider />}
            <ListItem disablePadding>
              <ListItemButton
                selected={user.id === currentDemoUser.id}
                onClick={() => switchDemoUser(user)}
                sx={{ py: 1, px: 1.5 }}
              >
                {user.id === currentDemoUser.id && (
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                )}
                <ListItemText
                  inset={user.id !== currentDemoUser.id}
                  primary={<Typography variant="body2" fontWeight={500}>{user.name}</Typography>}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">{user.role} · </Typography>
                      {user.access.map((a) => (
                        <Chip
                          key={a}
                          label={a.charAt(0).toUpperCase() + a.slice(1)}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                        />
                      ))}
                    </Box>
                  }
                  sx={{ my: 0 }}
                />
              </ListItemButton>
            </ListItem>
          </Fragment>
        ))}
      </List>

      <Divider sx={{ mb: 2 }} />

      {/* Simulate inbound referrals */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Simulate a new inbound referral arriving automatically. Use "+ New Referral" to demo
        phone and fax intake manually.
      </Typography>
      <Stack spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<EmailIcon />}
          onClick={() => simulateIntake(ReferralSource.EMAIL)}
        >
          Simulate Email Referral
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ArticleIcon />}
          onClick={() => simulateIntake(ReferralSource.EFAX)}
        >
          Simulate E-Fax Referral
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CodeIcon />}
          onClick={() => simulateIntake(ReferralSource.API)}
        >
          Simulate API Referral
        </Button>
        <Divider sx={{ my: 1 }} />
        <Button
          fullWidth
          variant="text"
          color="error"
          startIcon={<RestartAltIcon />}
          onClick={() => { resetAll(); clearAll(); clearIntakeQueue(); }}
        >
          Reset All Referrals
        </Button>
      </Stack>
    </Box>
  );
};
